import { GoogleService } from '../../../services/google.service';
import { UtilsService } from '../../../services/utils.service';
import { InputComponent } from '../input/input.component';
import { LocationType } from '../../../types/location.type';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ICONS } from '../../../constants/constants';
import { GoogleMapComponent } from '../../../modules/shared/google-map/google-map.component';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-location-autocomplete',
  templateUrl: './location-autocomplete.component.html',
  styleUrls: ['./location-autocomplete.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocationAutocompleteComponent
  implements OnInit, OnChanges, OnDestroy
{
  protected readonly ICONS = ICONS;

  constructor(private googleService: GoogleService) {}

  @Input() public fc: UntypedFormControl;
  @Input() public label: string = '';
  @Input() public mapRef: GoogleMapComponent | undefined = undefined;

  @ViewChild(InputComponent) input: InputComponent;

  private map: google.maps.Map | undefined = undefined;
  private subscriptions: Subscription = new Subscription();
  private markerElement: google.maps.marker.AdvancedMarkerElement | undefined =
    undefined;

  ngOnInit(): void {
    this.subscriptions.add(
      this.fc.valueChanges.subscribe((searchString: string = '') => {
        const location = UtilsService.parseLocation(searchString);

        if (location) {
          this.setValue(location);
        }
      })
    );

    this.fc.registerOnDisabledChange((isDisabled) => {
      if (isDisabled) {
        this.removeMarker();
      }
    });
  }

  private removeMarker(): void {
    if (this.markerElement) {
      this.markerElement.map = null;
    }
  }

  ngOnChanges({ mapRef }: SimpleChanges): void {
    if (!this.map && mapRef && this.mapRef?.mapRef) {
      GoogleService.getPlaces().then((places) => {
        const autocomplete = new places.Autocomplete(
          this.input.input.nativeElement,
          { types: [] }
        );

        google.maps.event.addListener(autocomplete, 'place_changed', () => {
          const place = autocomplete.getPlace();
          const location = this.getLocationFromPlace(place);

          this.setValue(location);
        });

        GoogleService.getMaps().then((maps) => {
          this.map = new maps.Map(
            this.mapRef?.mapRef.nativeElement as HTMLElement,
            {
              center: {
                lat: 56.9676941,
                lng: 24.1056221,
              },
              zoom: 5,
              mapId: UtilsService.getUniqueId(),
            }
          );

          this.configureAutoSelect(this.map);
        });
      });
    }
  }

  private async configureAutoSelect(map: google.maps.Map): Promise<void> {
    const location = UtilsService.parseLocation(this.fc.value ?? '');

    if (location !== undefined) {
      this.setValue(location);
    }

    const geocoder = new (await GoogleService.getGeocoder()).Geocoder();

    google.maps.event.addListener(map, 'click', (e: any) => {
      const latLng = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
      };

      geocoder
        .geocode({ location: latLng })
        .then((response) => {
          if (response.results.length > 0) {
            const place = response.results[0];
            const location: LocationType = this.getLocationFromPlace(
              place,
              true
            );

            this.setValue(location);
          }
        })
        .catch(() => {
          //TODO: add alert when error monitoring will be setup
        });
    });
  }

  setValue(value: LocationType): void {
    this.fc.setValue(value, { emitEvent: false });
    this.input.input.nativeElement.value =
      UtilsService.getLocationDisplayName(value);
    this.setMarker(value);
  }

  async setMarker(value: LocationType): Promise<void> {
    if (!this.map) {
      return;
    }

    this.removeMarker();

    const { lat, lng } = value;

    const position = {
      lat: Number(lat),
      lng: Number(lng),
    };

    const marker = await GoogleService.getMarker();

    this.markerElement = new marker.AdvancedMarkerElement({
      position,
      map: this.map,
    });

    this.map.panTo(position);
    this.map.setZoom(12);
  }

  getLocationFromPlace(
    place: google.maps.places.PlaceResult,
    isPointOnMap: boolean = false
  ): LocationType {
    return {
      name: place.name ?? place.formatted_address ?? '',
      formatted_address: place.formatted_address || '',
      place_id: place.place_id || '',
      lat: `${place.geometry?.location?.lat() ?? 0}`,
      lng: `${place.geometry?.location?.lng() ?? 0}`,
      is_point_on_map: isPointOnMap,
      ...UtilsService.getAddressObject(place.address_components || []),
    };
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
